import React, { CSSProperties, useContext, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { Element } from 'react-scroll/modules';
import Slider from 'react-slick';
import JobOpeningTextListItem from '@components/common/jobOpeningTextListItem';
import { JobOpeningTextListItemVO } from '@domain/vo/common/JobOpeningTextListItemVO';
import { JobPostModalContext } from '@components/common/jobPostModal/JobPostModalWrap';
import SliderButton from '@components/common/sliderButton';

import useMoveToComponentByQuery from '@utils/hooks/useMoveToComponentByQuery';

import classnames from 'classnames/bind';
import styles from './jobPostList.module.scss';
import Banner from '@components/acca/test/banner';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { useAccPostingPosition } from '@repository/job-postings/useJobPostings';

const cx = classnames.bind(styles);

// 역검 활용 공고 영역 id
export const JobPostingAreaName = 'JobPostingArea';

interface SlideArrowButtonProps {
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
}

const JobPostList = () => {
  const { query } = useRouter();
  const dragging = useRef(false);
  const { onOpenJobPostModal } = useContext(JobPostModalContext);
  const { data: positionListData } = useAccPostingPosition(24);
  const { data: userData } = useUserInfo();
  const isNotJobPostings = positionListData?.jobPostings.length === 0;
  const isLogin = !!userData?.id;

  const PrevArrow = (props: SlideArrowButtonProps) => {
    const { className, style, onClick } = props;

    return (
      <SliderButton
        type={'prev'}
        slickClass={className}
        handleClick={onClick}
        style={{ ...style }}
        customClassName={cx('btnPrev')}
      />
    );
  };

  const NextArrow = (props: SlideArrowButtonProps) => {
    const { className, style, onClick } = props;

    return (
      <SliderButton
        type={'next'}
        slickClass={className}
        handleClick={onClick}
        style={{ ...style }}
        customClassName={cx('btnNext')}
      />
    );
  };

  const settings = {
    infinite: false,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    slidesToShow: 1,
    speed: 300,
    rows: 2,
    slidesPerRow: 4,
    cssEase: 'ease-in-out',
    draggable: true,
    beforeChange: () => (dragging.current = true),
    afterChange: () => (dragging.current = false),
  };

  //이전 공고, 다음 공고 표시 이동하기 위한 용도
  const allPositionList =
    positionListData?.jobPostings?.map((item) => {
      return { key: `${item.sn}`, sn: item.sn, name: item.companyName };
    }) ?? [];

  const jobPostListItems =
    positionListData?.jobPostings?.map((item) => {
      const positionData = new JobOpeningTextListItemVO({
        ...item,
      });

      return (
        <div className={cx('listItemWrap')} key={item.sn}>
          <JobOpeningTextListItem
            positionData={positionData}
            onClickCard={() => {
              if (dragging.current) return;
              onOpenJobPostModal(`${item.sn}`, item.sn, allPositionList);
            }}
          />
        </div>
      );
    }) ?? [];

  const timerId = useMoveToComponentByQuery(JobPostingAreaName, {
    enable: !isNotJobPostings && query.move === 'jobPostings',
    removeQuery: 'move',
  });

  useEffect(() => {
    if (!timerId || query.move === 'jobPostings') {
      return;
    }

    clearTimeout(timerId);
  }, [timerId, query.move]);

  if (!positionListData?.jobPostings) return null;

  return (
    <Element name={JobPostingAreaName} className={cx('section', 'sectionArea')}>
      {!isLogin && <Banner />}

      <div className={cx('sectionInner')}>
        <div className={cx('jobPostListArea')}>
          <strong className={cx('sectionTitle')}>역검 활용 공고</strong>

          <div className={cx('jobPostList')}>
            <Slider {...settings}>{jobPostListItems}</Slider>
          </div>
        </div>
      </div>
    </Element>
  );
};

export default JobPostList;
